import React, { useState, useEffect } from 'react';
import { ICellsSelection } from 'interfaces/review/ICellsSelection';
import { Play } from 'models/tagging/Play';
import { TaggingFlow } from 'pages/Tagging/components/TaggingFlow/TaggingFlow';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { Button } from '@trackman/web-shared-components';
import { IApiPlayWithBallFinals } from 'interfaces/session/IApiPlayWithBallFinals';
import { DataKey } from 'enums/DataKey';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { ReviewDataRow } from 'models/review/ReviewDataRow/ReviewDataRow';
import { Content, Editor, Footer, StyledModal } from './PlayEditorStyles';

interface IProps {
  visible: boolean;
  onDismissCallback: () => void;
  rows: ReviewDataRow[];
  selection: ICellsSelection | undefined;
  sessionMetadata: IApiSessionMetadataUpdated;
  updateRow?: (index: number, dataKey: DataKey, newValue: any) => void;
}

const PlayEditor: React.FC<IProps> = ({ visible, onDismissCallback, rows, selection, updateRow, sessionMetadata }) => {
  const [playId, setPlayId] = useState<string>('');
  const [play, setPlay] = useState<Play | undefined>(undefined);
  let COLUMN_KEYS: DataKey[] = [];

  if (sessionMetadata.isPlayerTrackingEnabled) {
    COLUMN_KEYS = [
      DataKey.TaggedPitchType,
      DataKey.PitchCall,
      DataKey.HitType,
      DataKey.PlayResult,
      DataKey.OutsOnPlay,
      DataKey.RunsScored,
      DataKey.CatcherThrows,
      DataKey.FirstBase,
      DataKey.SecondBase,
      DataKey.ThirdBase,
      DataKey.FieldingSequence,
    ];
  } else {
    COLUMN_KEYS = [
      DataKey.TaggedPitchType,
      DataKey.PitchCall,
      DataKey.HitType,
      DataKey.PlayResult,
      DataKey.OutsOnPlay,
      DataKey.RunsScored,
      DataKey.CatcherThrows,
    ];
  }

  const getPlay = (id: string): void => {
    let datarow: ReviewDataRow;
    rows.forEach((row) => {
      if (row.id === id) {
        datarow = row;
        const apiPlay = datarow.toApiPlay(sessionMetadata);
        setPlay(mappingHelpers.apiPlayModelToPlayMap(apiPlay.play));
      }
    });
  };

  const updateRowsData = (): void => {
    if (play) {
      const apiPlay = mappingHelpers.playToApiPlayModelMap(play, sessionMetadata);
      const apiReviewPlay = {
        play: apiPlay,
        ballFinals: [],
        isChanged: false,
      } as IApiPlayWithBallFinals;
      const dataRow = new ReviewDataRow(apiReviewPlay, '', '');

      COLUMN_KEYS.forEach((element) => {
        updateRow && updateRow(selection!.fromRowIndex, element, dataRow[element]);
      });
    }
    onDismissCallback();
  };

  useEffect(() => {
    if (visible) {
      if (selection) {
        setPlayId(rows[selection.fromRowIndex].id);
      }
      if (playId) {
        getPlay(playId);
      }
    } else {
      setPlay(undefined);
      setPlayId('');
    }
  }, [visible, playId]);

  return (
    <StyledModal open={visible} onClose={onDismissCallback} title='Edit Play' disableEscapeKeyDown>
      <Content>
        <Editor>{play === undefined ? '' : <TaggingFlow review selectedPlay={play} />}</Editor>
        <Footer>
          <Button intent='tertiary' label='Cancel' onClick={onDismissCallback} width='160px' />
          <Button intent='primary' label='Edit play' onClick={(): void => updateRowsData()} width='160px' />
        </Footer>
      </Content>
    </StyledModal>
  );
};

export default PlayEditor;
